/**
 * Ward/Unit Management Component
 * Displays and manages wards (Aged Care) or units/houses (NDIS) for a facility
 */

import React, { useState, useEffect } from 'react';
import { DoorOpen, Plus, Edit, Trash2, ChevronDown, ChevronRight, Home, Building2 } from 'lucide-react';
import WardService from '../../services/api/WardService';
import FacilityService from '../../services/api/FacilityService';
import { useConfirm } from '../Common/ConfirmDialog';

const WardManagement = ({ facilityId, onWardChange, onAddWard, onEditWard }) => {
  const confirm = useConfirm();
  const [wards, setWards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [facility, setFacility] = useState(null);
  
  useEffect(() => {
    const loadFacility = async () => {
      try {
        const result = await FacilityService.getById(facilityId);
        if (result.success) {
          setFacility(result.data);
        }
      } catch (error) {
        console.error('Error loading facility:', error);
      }
    };
    if (facilityId) {
      loadFacility();
    }
  }, [facilityId]);
  
  // Determine terminology based on facility type
  const getTerminology = () => {
    if (!facility) {
      return {
        singular: 'Unit',
        plural: 'Units',
        addButton: 'Add Unit',
        noItems: 'No units found. Click "Add Unit" to create one.',
        loading: 'Loading units...',
        icon: Building2
      };
    }
    
    const isNDIS = facility.primaryType === 'ndis' || facility.facilityTypes?.includes('ndis');
    const isAgedCare = facility.primaryType === 'agedcare' || facility.facilityTypes?.includes('agedcare');
    
    if (isNDIS && !isAgedCare) {
      return {
        singular: 'Unit',
        plural: 'Units',
        addButton: 'Add Unit',
        noItems: 'No units found. Click "Add Unit" to create one.',
        loading: 'Loading units...',
        icon: Home
      };
    } else if (isAgedCare && !isNDIS) {
      return {
        singular: 'Ward',
        plural: 'Wards',
        addButton: 'Add Ward',
        noItems: 'No wards found. Click "Add Ward" to create one.',
        loading: 'Loading wards...',
        icon: DoorOpen
      };
    } else {
      // Dual facility - use generic term
      return {
        singular: 'Unit',
        plural: 'Units',
        addButton: 'Add Unit',
        noItems: 'No units found. Click "Add Unit" to create one.',
        loading: 'Loading units...',
        icon: Building2
      };
    }
  };

  const terminology = getTerminology();
  const Icon = terminology.icon;

  useEffect(() => {
    if (expanded) {
      loadWards();
    }
  }, [expanded, facilityId]);

  const loadWards = async () => {
    setLoading(true);
    try {
      const result = await WardService.getByFacilityId(facilityId);
      if (result.success) {
        setWards(result.data || []);
      }
    } catch (error) {
      console.error('Error loading wards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (wardId) => {
    if (!await confirm({ title: `Delete ${terminology.singular}`, message: `Are you sure you want to delete this ${terminology.singular.toLowerCase()}?`, confirmLabel: 'Delete', variant: 'danger' })) {
      return;
    }

    try {
      const result = await WardService.delete(wardId);
      if (result.success) {
        loadWards();
        if (onWardChange) onWardChange();
      }
    } catch (error) {
      console.error('Error deleting ward:', error);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-gray-200">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full mb-3 text-left"
      >
        <h5 className="font-semibold text-gray-700 flex items-center gap-2">
          <Icon className="w-4 h-4" />
          {terminology.plural} ({wards.length})
        </h5>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-gray-600" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-600" />
        )}
      </button>

      {expanded && (
        <>
          <div className="flex justify-end mb-3">
            <button
              onClick={() => onAddWard && onAddWard(facilityId)}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {terminology.addButton}
            </button>
          </div>

          {loading ? (
            <p className="text-sm text-gray-500 py-4">{terminology.loading}</p>
          ) : wards.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">{terminology.noItems}</p>
          ) : (
            <div className="space-y-2">
              {wards.map((ward) => (
                <div
                  key={ward.id}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">{ward.name}</span>
                      {ward.type && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {ward.type}
                        </span>
                      )}
                      {ward.code && (
                        <span className="text-xs text-gray-500">({ward.code})</span>
                      )}
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        ward.status === 'active' ? 'bg-green-100 text-green-800' :
                        ward.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {ward.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                      <span>Capacity: {ward.capacity}</span>
                      <span>Occupied: {ward.occupied}</span>
                      <span>Available: {ward.available || (ward.capacity - ward.occupied)}</span>
                      {ward.careLevel && (
                        <span>Care Level: {ward.careLevel}</span>
                      )}
                      {ward.specialization && (
                        <span>Specialization: {ward.specialization}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => onEditWard && onEditWard(ward)}
                      className="p-1.5 hover:bg-blue-50 rounded transition-colors"
                      title={`Edit ${terminology.singular}`}
                    >
                      <Edit className="w-4 h-4 text-blue-600" />
                    </button>
                    <button
                      onClick={() => handleDelete(ward.id)}
                      className="p-1.5 hover:bg-red-50 rounded transition-colors"
                      title={`Delete ${terminology.singular}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WardManagement;

